function groupByLoad(locations) {
  const sorted = [...locations]
    .sort((a, b) => new Date(a.LocationDate) - new Date(b.LocationDate))
  const groups = []
  for (const loc of sorted) {
    const isManual = loc.Source === 'Manual' || !loc.LoadID
    const last = groups[groups.length - 1]
    if (!isManual && last && last[0].LoadID === loc.LoadID) {
      last.push(loc)
    } else {
      groups.push([loc])
    }
  }
  return groups
}

/**
 * Returns a Map<LoadID, StaleInfo> for loads whose empty mileage is stale.
 *
 * StaleInfo = {
 *   firstStopDate,            // LocationDate of the first stop of the stale load
 *   prevLastTruckLocationID,  // TruckLocationID of the actual last stop before this load started
 *   dispatchRefStop,          // The stop object that was used at dispatch (may be null)
 *   actualLastStop,           // The globally latest stop (any load) before this load's first stop
 * }
 *
 * Uses each load's true first stop (not the first stop of each contiguous group) so that
 * interleaved loads don't produce false stale entries for their non-first groups.
 */
export function getStaleLoadMap(locations) {
  if (!locations || locations.length === 0) return new Map()

  // Find the first stop per load (earliest LocationDate)
  const firstStopByLoad = new Map()
  for (const loc of locations) {
    if (!loc.LoadID || loc.Source === 'Manual') continue
    const existing = firstStopByLoad.get(loc.LoadID)
    if (!existing || new Date(loc.LocationDate) < new Date(existing.LocationDate)) {
      firstStopByLoad.set(loc.LoadID, loc)
    }
  }

  // All non-manual stops sorted chronologically
  const allSorted = locations
    .filter(loc => loc.LoadID && loc.Source !== 'Manual')
    .sort((a, b) => new Date(a.LocationDate) - new Date(b.LocationDate))

  const staleMap = new Map()

  for (const [loadID, firstStop] of firstStopByLoad) {
    const dispatchRefID = firstStop.LastTruckLocationID
    if (!dispatchRefID) continue

    // Find the globally latest stop (from any other load) that occurred strictly
    // before this load's first stop — handles interleaved loads correctly.
    const firstStopDate = new Date(firstStop.LocationDate)
    let actualLastStop = null
    for (let i = allSorted.length - 1; i >= 0; i--) {
      const s = allSorted[i]
      if (s.LoadID === loadID) continue
      if (new Date(s.LocationDate) < firstStopDate) {
        actualLastStop = s
        break
      }
    }

    if (actualLastStop && actualLastStop.TruckLocationID !== dispatchRefID) {
      const dispatchRefStop = allSorted.find(s => s.TruckLocationID === dispatchRefID) ?? null
      staleMap.set(loadID, {
        firstStopDate: firstStop.LocationDate,
        prevLastTruckLocationID: actualLastStop.TruckLocationID,
        dispatchRefStop,
        actualLastStop,
      })
    }
  }

  return staleMap
}

export function buildRouteSegments(locations) {
  if (!locations || locations.length === 0) return []

  // Sort without filtering so original group indices are preserved for Mileages CSV alignment.
  const groups = groupByLoad(locations)

  if (groups.reduce((n, g) => n + g.length, 0) < 2) return []

  function hasCoords(loc) {
    return loc.Latitude != null && loc.Longitude != null &&
      !isNaN(parseFloat(loc.Latitude)) && !isNaN(parseFloat(loc.Longitude))
  }

  function pt(loc) {
    return [parseFloat(loc.Latitude), parseFloat(loc.Longitude)]
  }

  const segments = []

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i]

    // Between-group segment (empty or manual) arriving at this group from the previous.
    if (i > 0) {
      const prevGroup = groups[i - 1]

      // Use last valid-coord stop of prevGroup for drawing; chronological last for stale check.
      let prevLast = null
      for (let k = prevGroup.length - 1; k >= 0; k--) {
        if (hasCoords(prevGroup[k])) { prevLast = prevGroup[k]; break }
      }
      let currFirst = null
      for (let k = 0; k < group.length; k++) {
        if (hasCoords(group[k])) { currFirst = group[k]; break }
      }

      if (prevLast && currFirst) {
        const fromPt = pt(prevLast)
        const toPt = pt(currFirst)
        const midpoint = [(fromPt[0] + toPt[0]) / 2, (fromPt[1] + toPt[1]) / 2]

        const isManualConn =
          (prevGroup[0].Source === 'Manual' || !prevGroup[0].LoadID) ||
          (group[0].Source === 'Manual' || !group[0].LoadID)
        const type = isManualConn ? 'manual' : 'empty'

        let isStale = false
        if (type === 'empty' && group[0].LastTruckLocationID) {
          // group[0].LastTruckLocationID = the last stop of prevGroup recorded at dispatch time.
          // Stale when prevGroup's actual chronological last stop differs from that reference.
          isStale = prevGroup[prevGroup.length - 1].TruckLocationID !== group[0].LastTruckLocationID
        }

        const miles = type === 'empty' && group[0].EmptyMiles != null ? parseFloat(group[0].EmptyMiles) : null
        const loadNum = type === 'empty' && group[0].LoadNumber ? `#${group[0].LoadNumber}` : null
        const milesStr = miles != null && miles > 0 ? `${miles.toFixed(1)} mi empty` : null
        const label = [loadNum, milesStr].filter(Boolean).join(' • ') || null

        segments.push({from: fromPt, to: toPt, midpoint, type, isStale, miles, label})
      }
    }

    // Within-group on-load segments.
    // j is the original stop index — used directly as the Mileages CSV index.
    // Skipping invalid-coord stops via `continue` preserves index alignment.
    if (group[0].Source === 'Manual' || !group[0].LoadID || group.length < 2) continue

    const mileages = group[0].Mileages
      ? group[0].Mileages.split(',').map(v => parseFloat(v.trim()))
      : []

    for (let j = 0; j < group.length - 1; j++) {
      const from = group[j]
      const to = group[j + 1]
      if (!hasCoords(from) || !hasCoords(to)) continue

      const fromPt = pt(from)
      const toPt = pt(to)
      const midpoint = [(fromPt[0] + toPt[0]) / 2, (fromPt[1] + toPt[1]) / 2]

      const miles = mileages[j] != null && !isNaN(mileages[j]) ? mileages[j] : null
      const loadNum = group[0].LoadNumber ? `#${group[0].LoadNumber}` : null
      const milesStr = miles != null ? `${miles.toFixed(1)} mi loaded` : null
      const label = [loadNum, milesStr].filter(Boolean).join(' • ') || null

      segments.push({from: fromPt, to: toPt, midpoint, type: 'onLoad', isStale: false, miles, label})
    }
  }

  return segments
}
